/**
 * Wire shape for a `chat.task` row from the corteva-chat-api `/chat/tasks`
 * REST resource (corteva-chat-api ADR 056). The wire is camelCase and the
 * client sends/parses it directly; the server owns `id`, `userId`,
 * `createdAt`, and `updatedAt`. Replaces the old `TaskEntry` +
 * `LongTaskBinding` composite. Predicates and status types are imported from
 * TaskRegistry; do not redefine them here.
 */
import type {
  ResultLink,
  TaskKind,
  TaskStatusValue,
  TerminalTaskStatus,
} from "./TaskRegistry";
import { getTaskKind, shouldStopPolling } from "./TaskRegistry";

export interface Task {
  id: string;
  userId: string;
  threadId: string;
  kind: TaskKind;
  serverUrl: string | null;
  groupName: string | null;
  /**
   * Resolved status-tool name and OAuth scopes for the job's `(server, group)`,
   * captured at submit so the poller reads them from the durable row instead of
   * the session-only lifecycle map (which is rebuilt on a persona switch). Set
   * on `kind: "long-task"` rows; `oauthScopes` shares the backend column with
   * the MCP-task writer (corteva-chat-api ADR 059), disjoint by `kind`.
   * The poller reads these two columns and nothing else; there is no fallback to
   * the live map. Create-time immutable (absent from `TaskPatch`).
   */
  statusTool?: string | null;
  oauthScopes?: string | null;
  /**
   * The poller's key for this row. Source differs by `kind`, but the role is
   * the same: for `long-task` it's the submit envelope's `job_id`; for
   * `mcp-task` it's the row's own API `task_id` (so the two are equal by definition).
   */
  jobId: string;
  /** ID to for long-tasks to correlate logs between the MCP and external system */
  correlationId?: string | null;
  /**
   * `ownerJobId` is the ID of a long task this MCP task is associated with.
   * The final phase of a long task involves creating an MCP task to poll for
   * the final result of the long task.
   * `ownerJobId` will only ever be set for MCP tasks (`kind: "mcp-task"`).
   */
  ownerJobId?: string | null;
  toolName: string;
  displayName?: string;
  status: TaskStatusValue;
  statusReason?: string;
  statusRaw?: string;
  statusCachedAt?: string;
  /**
   * The per-report links a fetch delivered, and nothing else: set means "these
   * are the links", absent or NULL means no fetch has delivered any. The backend
   * sends the column unconditionally, so an unset one arrives as `null` rather
   * than omitted, and the two must be treated alike.
   */
  resultLinks?: ResultLink[] | null;
  archiveUrl?: string;
  /**
   * For `kind: "mcp-task"` fetch-tracker rows: the task ID of the tracker itself
   * (= its mcp-task task_id). The owning long-task row's jobId is stored in
   * `ownerJobId` to resolve the tracker→owner link.
   */
  fetchTaskId?: string;
  /** Poll interval in ms for MCP tasks; absent for long-task rows (interval derived from age). */
  pollInterval?: number;
  createdAt: string;
  /** Server-maintained last-modified time (orders the GET list). */
  updatedAt?: string;
  /**
   * Inject-once claim column. Set only by the server via the claim-terminal
   * endpoint; never sent on the generic PATCH (absent from `TaskPatch`).
   */
  terminalDirectiveInjectedAt?: string;
  terminalSeenAt?: string;
  dismissed?: boolean;
  /** Per-session polling state; cache-only, never committed to the server. */
  pollExhausted?: boolean;
  /**
   * Confirmed active against a live source (a successful non-terminal poll)
   * this session; cache-only, never on the wire. MCP-task-only: the badge counts a
   * non-terminal MCP task only once this is set, so a reload-hydrated row whose
   * persisted status is stale-active is not counted until a poll proves it
   * genuinely active. Needed because `GET /chat/tasks` (list) returns the persisted
   * row without refreshing it, and an MCP task's terminal status reaches that row
   * only when a poll observes it (there is no autonomous server-side refresh). A
   * task that completed while unobserved — tab closed, or poll budget exhausted —
   * therefore stays persisted non-terminal indefinitely, so without this gate the
   * badge would count it on every later session with nothing able to clear it.
   * Long-tasks PATCH their terminal status durably, so they bypass the gate.
   */
  liveConfirmed?: boolean;
}

/**
 * True when a long-task job is terminal, its directive has been injected, and
 * the user has not yet seen it. Drives the sidebar's per-row and
 * clipboard-button unread dots. Gating on `terminalDirectiveInjectedAt` limits
 * the signal to genuine completions (not just any terminal entry).
 */
export function isUnreadTerminalLongTask(task: Task): boolean {
  return (
    getTaskKind(task) === "long-task" &&
    !task.dismissed &&
    shouldStopPolling(task.status) &&
    !!task.terminalDirectiveInjectedAt &&
    !task.terminalSeenAt
  );
}

/**
 * Summarizes the unread-completion signal for a set of rows. Shared by the
 * per-row sidebar dot and the cross-chat clipboard-button dot.
 */
export function getUnreadCompletionIndicator(tasks: Task[]): {
  hasUnread: boolean;
  hasFailed: boolean;
} {
  const unread = tasks.filter(isUnreadTerminalLongTask);
  return {
    hasUnread: unread.length > 0,
    hasFailed: unread.some(
      (t) => t.status === "failed" || t.status === "cancelled"
    ),
  };
}

/**
 * True for a results-fetch tracker: the hidden `mcp-task` row that polls a long
 * task's background fetch. `fetchTaskId` is set on trackers and nothing else.
 */
export function isFetchTracker(task: Task): boolean {
  return task.fetchTaskId !== undefined && task.fetchTaskId !== null;
}

/**
 * True while the fetch tracker owned by `jobId` is still running. Withholds a
 * completed job's payoff until the background fetch concludes.
 *
 * A tracker's `ownerJobId` names the long task it fetches for. Non-tracker rows
 * are skipped rather than compared, since their `ownerJobId` is unrelated.
 */
export function isFetchTrackerInFlight(
  tasks: Task[],
  jobId: string | undefined
): boolean {
  if (!jobId) return false;
  return tasks.some(
    (task) =>
      isFetchTracker(task) &&
      task.ownerJobId === jobId &&
      !shouldStopPolling(task.status)
  );
}

/** A row whose status has been checked as terminal, for the terminal-only APIs. */
export type TerminalTask = Task & { status: TerminalTaskStatus };

/**
 * Input shape for POST /chat/tasks. Server assigns id/userId/createdAt/updatedAt.
 * `terminalSeenAt` and `terminalDirectiveInjectedAt` are also omitted: the
 * backend create schema is `extra="forbid"` and defines neither (the former is
 * patch-only, the latter is the claim-terminal column), so keeping them off the
 * TS type matches the wire allowlist and stops a future caller from spreading a
 * full `Task` into a create and tripping a 422.
 */
export type TaskCreate = Omit<
  Task,
  | "id"
  | "userId"
  | "createdAt"
  | "updatedAt"
  | "pollExhausted"
  | "liveConfirmed"
  | "terminalSeenAt"
  | "terminalDirectiveInjectedAt"
>;

/**
 * Input shape for PATCH /chat/tasks/:id. `terminalDirectiveInjectedAt` is
 * excluded: it is the inject-once claim column, written only by the
 * claim-terminal endpoint. Carrying it here would defeat the atomic claim.
 */
export type TaskPatch = Partial<
  Pick<
    Task,
    | "status"
    | "statusReason"
    | "statusRaw"
    | "statusCachedAt"
    | "resultLinks"
    | "archiveUrl"
    | "terminalSeenAt"
    | "dismissed"
  >
>;

/**
 * Reconciles a freshly-fetched server task list against the existing cache,
 * preserving fields the server is not authoritative for. MCP task `status` is
 * written cache-only (ADR 056), so a stale server `pending` must not demote a
 * terminal cache row; `pollExhausted` and `liveConfirmed` are client-only and
 * never on the wire. Applied inside the tasks query's `queryFn` so every fetch
 * (focus refetch included) is non-destructive without touching `setQueryData`
 * writes. Rows absent from `next` are dropped; the server list is authoritative
 * for membership.
 *
 * A set `terminalDirectiveInjectedAt` is preserved by the same rule
 * {@link applyServerTaskRow} applies on the mutation path, so the GET path cannot
 * disagree with it: the column is monotonic server-side (set once from NULL by
 * the conditional claim UPDATE, never reset), so a fetch that returns it null is
 * reporting a pre-claim read, not a clear.
 */
export function mergeTasksPreservingClientState(
  prev: Task[] | undefined,
  next: Task[]
): Task[] {
  if (!prev) return next;
  const prevById = new Map(prev.map((j) => [j.id, j]));
  return next.map((serverJob) => {
    const cached = prevById.get(serverJob.id);
    if (!cached) return serverJob;
    const merged: Task = { ...serverJob };
    if (cached.pollExhausted !== undefined) {
      merged.pollExhausted = cached.pollExhausted;
    }
    if (cached.liveConfirmed !== undefined) {
      merged.liveConfirmed = cached.liveConfirmed;
    }
    if (
      shouldStopPolling(cached.status) &&
      !shouldStopPolling(serverJob.status)
    ) {
      merged.status = cached.status;
    }
    if (
      cached.terminalDirectiveInjectedAt &&
      !serverJob.terminalDirectiveInjectedAt
    ) {
      merged.terminalDirectiveInjectedAt = cached.terminalDirectiveInjectedAt;
    }
    return merged;
  });
}

/**
 * Replaces a cached row with a server row from a mutation response, but never
 * lets the incoming row drop a `terminalDirectiveInjectedAt` (the inject-once
 * claim) the cache already holds. The status PATCH and the claim PATCH are
 * independent requests whose responses can resolve in either order; the status
 * response reflects whatever the row held when it executed server-side and may
 * carry a null claim column. Preserving the set claim keeps a late status
 * response from clobbering the just-won claim out of the cache.
 */
export function applyServerTaskRow(cached: Task, incoming: Task): Task {
  const merged: Task = { ...cached, ...incoming };
  if (
    cached.terminalDirectiveInjectedAt &&
    !incoming.terminalDirectiveInjectedAt
  ) {
    merged.terminalDirectiveInjectedAt = cached.terminalDirectiveInjectedAt;
  }
  return merged;
}
